import { Map, Set } from 'immutable';
import { connect } from 'react-redux';
import ImmutablePropTypes from 'react-immutable-proptypes';
import PropTypes from 'prop-types';
import React from 'react';
import Switch from 'react-switch';
import classNames from 'classnames';
import moment from 'moment';

import { BottomModal, PromptModal } from '../../components/Modal';
import { addNotification, removeNotification } from '../../../../ducks/notifications';
import { getImageUrl, navigate, navigatePush } from '../../../../helpers';
import { isMobile } from '../../../../mobile/helpers';
import { readMessages } from '../../../../ducks/event';
import { update } from '../../../../ducks/app';
import BackgroundImage from '../../components/BackgroundImage';
import Content from '../../components/Content';
import Header from '../../components/Header';
import Highlight from './Highlight';
import Loader from '../../components/Loader';
import Subheader from '../../components/Subheader';
import styles from './start.scss';

const propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    shortcode: PropTypes.string,
    image_id: PropTypes.string,
    logo_id: PropTypes.string,
    url: PropTypes.string,
    seatmap_published: PropTypes.bool,
    local_end_time: PropTypes.string,
    local_start_time: PropTypes.string,
    pub_exhibitors: PropTypes.array,
    activities: PropTypes.array,
    ticker_messages: PropTypes.array,
  }),
  onRequestOpenFn: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    stackPush: PropTypes.func.isRequired,
  }).isRequired,
  maps: PropTypes.shape({
    filter: PropTypes.func.isRequired,
  }).isRequired,
  achievements: ImmutablePropTypes.list,
  dispatch: PropTypes.func.isRequired,
  readMessagesList: PropTypes.instanceOf(Set),
  match: PropTypes.shape({
    params: PropTypes.shape({
      shortcode: PropTypes.string.isRequired,
      option: PropTypes.string,
    }),
    url: PropTypes.string.isRequired,
  }).isRequired,
  eventNotificationsEnabled: PropTypes.instanceOf(Map),
  notificationsLoading: PropTypes.bool,
  quizOpen: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
};

class Start extends React.Component {
  constructor(props) {
    super(props);
    const { match, eventNotificationsEnabled } = props;
    const { shortcode } = match && match.params;
    this.state = {
      notificationPromptOpen: isMobile() && !eventNotificationsEnabled.has(shortcode),
    };
  }

  openMessages = () => {
    const { history, match } = this.props;
    navigate(history, `${match.url}/ticker`);
  }

  closeMessages = () => {
    const {
      dispatch,
      event,
      history,
      match,
    } = this.props;
    // When the user closes the messages modal, mark all messages
    // inside as read.
    dispatch(readMessages(event.ticker_messages.map((msg) => msg.id)));
    navigate(history, match.url.replace('/ticker', ''));
  }

  closeNotificationPrompt = () => {
    const { dispatch, event, eventNotificationsEnabled } = this.props;
    dispatch(update(
      { eventNotificationsEnabled: eventNotificationsEnabled.set(event.shortcode, false) },
    ));
    this.setState({
      notificationPromptOpen: false,
    });
  }

  activateNotifications = () => {
    const { dispatch, event, eventNotificationsEnabled } = this.props;
    dispatch(addNotification({
      id: `${event.id}`,
      type: 'event',
    }))
      .then((success) => {
        dispatch(update(
          { eventNotificationsEnabled: eventNotificationsEnabled.set(event.shortcode, success) },
        ));
        if (success) {
          this.setState({
            notificationPromptOpen: false,
          });
        }
      });
  }

  disableNotifications = () => {
    const { event, dispatch, eventNotificationsEnabled } = this.props;
    dispatch(removeNotification(`${event.id}`))
      .then(dispatch(update(
        { eventNotificationsEnabled: eventNotificationsEnabled.set(event.shortcode, false) },
      )));
  }

  render() {
    const {
      onRequestOpenFn,
      loading,
      event,
      history,
      readMessagesList,
      notificationsLoading,
      eventNotificationsEnabled,
      quizOpen,
      achievements,
      match,
    } = this.props;
    const {
      notificationPromptOpen,
    } = this.state;

    // Using Immutable sets here helps us calculate read/unread messages easier
    const allMessages = event && new Set(event.ticker_messages.map((msg) => msg.id));
    const unreadMessages = allMessages && allMessages.subtract(readMessagesList);
    const notificationsEnabled = event && eventNotificationsEnabled.get(event.shortcode) === true;
    const eventHasExhibitors = event && event.pub_exhibitors && event.pub_exhibitors.filter((pe) => ['exhibitor', 'sponsor'].includes(pe.type))?.length > 0;
    const eventHasFood = event && event.pub_exhibitors && event.pub_exhibitors.filter((pe) => pe.type === 'restaurant')?.length > 0;

    const maps = this.props.maps
      .filter((map) => event && map.get('published') && map.get('event') === event.id);

    return (
      <>
        <Header
          onMenuOpen={onRequestOpenFn}
          history={history}
          showUser
        >
          { event && event.name }
        </Header>
        <Content>
          {
            /* If no event in cache, loader should show as usual */
            !event && <Loader loading={loading} />
          }
          { event
            && (
              <>
                <div className={styles.hero}>
                  <BackgroundImage
                    src={getImageUrl(event.image_id, 'dyn_980')}
                    className={styles.bg}
                  >
                    <BackgroundImage
                      src={getImageUrl(event.logo_id)}
                      className={styles.logo}
                    />
                  </BackgroundImage>
                  { event && (
                  <div
                    className={styles.messageButton}
                    role="button"
                    tabIndex={0}
                    onClick={() => this.openMessages()}
                  >
                    <i className="far fa-bullhorn">
                      { unreadMessages.size > 0
                        && <div className={styles.unread} />}
                    </i>
                  </div>
                  )}
                </div>
                {
                /* But if there's already an event in cache, show the in the event info */
                event && <Loader loading={loading} />
              }
                { /* If the event has passed, say thank you and show an easy link
                 * back to all events */ }
                { moment().isAfter(moment(event.local_end_time))
                && (
                <Highlight
                  icon="heart"
                  action="Thank you!"
                  description={`${event.name} is now over, and we thank you for being a part of it.`}
                  button="Show upcoming events"
                  onClick={() => navigate(history, '/app')}
                  colors={{
                    icon: '#e74c3c',
                  }}
                />
                )}
                { quizOpen && (
                <Highlight
                  icon="comment-alt-check"
                  action="The quiz is live!"
                  description="Join now to compete against the rest of the crowd in some trivia!"
                  button="Join quiz"
                  onClick={() => navigatePush(history, 'games')}
                  glow
                  colors={{
                    icon: '#ff6600',
                    button: '#ff6600',
                  }}
                />
                )}
                { achievements && achievements.size > 0 && (
                <Highlight
                  icon="fad fa-trophy"
                  action="Hunt the achievements"
                  description={`There are ${achievements.size} achievements to gather. Are you up to the task?`}
                  button="Show achievements"
                  onClick={() => navigate(history, `/app/event/${event.shortcode}/achievements`)}
                  colors={{
                    icon: '#f1c40f',
                  }}
                />
                )}
                { event.activities && event.activities?.length > 0
                && (
                <Highlight
                  icon="calendar-alt"
                  action="Browse the schedule"
                  description="See everything happening and create your own schedule."
                  button="View activities"
                  onClick={() => navigate(history, `/app/event/${event.shortcode}/schedule`)}
                />
                )}
                { /* If the schedule isn't up and we still have more than a week left
                 * show a countdown highlight with a website link instead to fill up
                 * the space */ }
                { (!event.activities || !event.activities?.length) && event.url && moment(event.local_start_time).diff(moment(), 'days') >= 7
                && (
                <Highlight
                  icon="clock"
                  action={`Only ${moment(event.local_start_time).diff(moment(), 'days')} days left!`}
                  description="While we're working on getting all the content up, visit the event website for the latest news."
                  button="Visit website"
                  onClick={() => window.open(event.url)}
                />
                )}
                { maps.size > 0
                && (
                <Highlight
                  icon="map-marked"
                  action="Don't get lost"
                  description="See the venue map and the different areas and halls."
                  button="Show the maps"
                  onClick={() => navigate(history, `/app/event/${event.shortcode}/maps`)}
                />
                )}
                { event.seatmap_published
                && (
                <Highlight
                  icon="qrcode"
                  action="Time for check-in?"
                  description="Show your barcode and find your seat!"
                  button="Show tickets"
                  onClick={() => navigate(history, `/app/event/${event.shortcode}/ticket`)}
                />
                )}
                { event.shortcode === 'DHW19'
                && (
                <Highlight
                  icon="trophy"
                  action="Night City by Dreamland!"
                  description="Komplett Dreamland is back with a new mysterious name, check it out!"
                  button="Read more"
                  onClick={() => window.open('https://www.komplett.se/kampanj/61309/dreamhack')}
                  colors={{
                    icon: '#f7b912',
                    button: '#d9950a',
                  }}
                />
                )}
                { eventHasExhibitors
                && (
                <Highlight
                  icon="store"
                  action="Explore the expo"
                  description="The expo is filled with cool companies and gadgets."
                  button="View partners"
                  onClick={() => navigate(history, `/app/event/${event.shortcode}/exhibitors`)}
                />
                )}
                { eventHasFood
                && (
                <Highlight
                  icon="utensils-alt"
                  action="Feeling hungry?"
                  description="Checkout where you can get food and drinks."
                  button="View food & drinks"
                  onClick={() => navigate(history, `/app/event/${event.shortcode}/food`)}
                />
                )}
                <Highlight
                  icon=" fab fa-discord"
                  action="Join us on Discord"
                  description="Find friends & pugs and keep the discussion alive."
                  button="Join the Discord"
                  onClick={() => window.open('https://discord.gg/dreamhack')}
                  colors={{
                    icon: '#7289da',
                    button: '#7289da',
                  }}
                />
              </>
            )}
        </Content>
        { event
          && (
            <>
              <BottomModal
                isOpen={match.params.option === 'ticker'}
                onRequestClose={() => this.closeMessages()}
                shouldCloseOnOverlayClick
                buttonText="Close"
              >
                <div className={styles.subheader}>
                  <Subheader>
                    <div className={styles.notificationControl}>
                      <div>
                        { isMobile()
                          ? <span>Event notifications enabled</span>
                          : <span>Messages from DreamHack</span>}
                      </div>
                      { isMobile()
                      && (
                      <div className={styles.switch}>
                        { notificationsLoading
                          && (
                          <i className={classNames(
                            'far fa-spinner-third',
                            [styles.rotate],
                          )}
                          />
                          )}
                        <Switch
                          checked={notificationsEnabled}
                          onChange={notificationsEnabled
                            ? () => this.disableNotifications()
                            : () => this.activateNotifications()}
                          height={12}
                          width={32}
                          handleDiameter={18}
                          uncheckedIcon={false}
                          checkedIcon={false}
                          disabled={notificationsLoading}
                          onColor="#27ae60"
                        />
                      </div>
                      )}
                    </div>
                  </Subheader>
                </div>
                <div className={styles.messages}>
                  { event.ticker_messages && event.ticker_messages?.length > 0
                    ? (
                      <>
                        { event.ticker_messages.map((message) => (
                          <div
                            key={message.id}
                            className={styles.message}
                          >
                            <div className={styles.date}>
                              {moment.utc(message.created_at).local().fromNow()}
                            </div>
                            <div className={styles.title}>
                              {!readMessagesList.has(message.id)
                            && <span className={styles.new} />}
                              {message.header}
                            </div>
                            { message.content
                          && <div className={styles.content}>{message.content}</div>}
                          </div>
                        ))}
                      </>
                    )
                    : (
                      <div className={styles.noMessages}>
                        There are no messages at the moment. Important messages from
                        the DreamHack crew will show up here and send a notification
                        if you have enabled notifications on your device.
                      </div>
                    )}
                </div>
              </BottomModal>
              <PromptModal
                title="Get important information"
                message={`Do you want to enable notifications for messages regarding ${event.name}? We'll only send important information for all visitors. You can always change your preference later.`}
                onRequestClose={() => this.closeNotificationPrompt()}
                isOpen={notificationPromptOpen}
                action={{
                  onClick: () => this.activateNotifications(),
                  text:
                  notificationsLoading
                    ? (
                      <i className={classNames(
                        'far fa-spinner-third',
                        [styles.rotate],
                      )}
                      />
                    )
                    : <span>Activate</span>,
                }}
                buttonText="Nah, maybe later"
              />
            </>
          )}
      </>
    );
  }
}

Start.propTypes = propTypes;

function mapStateToProps(state) {
  return {
    loading: state.getIn(['event', 'loading']),
    event: state.getIn(['event', 'data']),
    maps: state.getIn(['maps', 'list']),
    readMessagesList: state.getIn(['event', 'readMessages']),
    notificationsLoading: state.getIn(['notifications', 'loading']),
    eventNotificationsEnabled: state.getIn(['app', 'eventNotificationsEnabled']),
    quizOpen: state.getIn(['app', 'settings', 'quizOpen']),
    achievements: state.getIn(['achievements', 'achievements']),
  };
}

export default connect(mapStateToProps)(Start);
